/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Button, Divider, Grid, Skeleton, Typography2 } from '@coursera/cds-core';
import { AIGenerateBrandedIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import ChipButton from 'bundles/search-common/components/experiments/filters2024/ChipButton';
import {
  AI_SUMMARY_CHIP_BUTTON_HEIGHT,
  AI_SUMMARY_COLLAPSED_HEIGHT,
  AI_SUMMARY_LINK_POSITION_PADDING,
  ON_SITE_SEARCH_PATH,
} from 'bundles/search-common/constants';

import _t from 'i18n!nls/search-common';

type Props = {
  summary?: string | React.ReactNode;
  suggestedQueries?: string[];
  isLoading?: boolean;
  onQueryClick?: (query: string) => void;
  showMockIndicator?: boolean; // Show indicator when using mock data
};

const styles = {
  container: css`
    margin-bottom: var(--cds-spacing-400);
    padding: var(--cds-spacing-100);
  `,
  header: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-200);

    svg {
      width: 24px;
      height: 24px;
    }
  `,
  summaryWrapper: (isExpanded: boolean, effectiveCollapsedHeight?: string) => css`
    position: relative;
    max-height: ${isExpanded ? '1000px' : effectiveCollapsedHeight || AI_SUMMARY_COLLAPSED_HEIGHT};
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
    ${!isExpanded && 'cursor: pointer;'}
  `,

  summaryText: css`
    margin: 0;
    margin-bottom: var(--cds-spacing-200);
  `,
  moreButton: css`
    padding: 0;
    text-decoration: none;
    color: var(--cds-color-interactive-primary);
    font-weight: 500;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  `,
  suggestedQueriesSection: css`
    margin-top: var(--cds-spacing-200);
  `,
  suggestedQueriesLabel: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  chipContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    margin-left: -3px;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain; /* Prevent scroll chaining */
    touch-action: pan-x; /* Only allow horizontal panning */

    /* Hide scrollbar */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  `,
  chipRow: css`
    display: flex;
    gap: var(--cds-spacing-100);
    flex-shrink: 0;
  `,
  skeletonContainer: css`
    min-height: ${AI_SUMMARY_COLLAPSED_HEIGHT};
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  skeletonHeader: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
    margin-bottom: var(--cds-spacing-200);
  `,
  chipSkeletonContainerWrapper: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-100);
    margin-left: -3px;
    margin-top: var(--cds-spacing-200);
    overflow-x: auto;
    overflow-y: hidden;

    /* Hide scrollbar */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  `,
  chipSkeletonContainer: css`
    align-items: center;
    display: flex;
    border-radius: 20px;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    margin: 0 3px;
    height: ${AI_SUMMARY_CHIP_BUTTON_HEIGHT};
    gap: 4px;
    border: 1px solid var(--cds-color-grey-100);
    background-color: var(--cds-color-neutral-background-primary);
    max-width: 300px;
    flex-shrink: 0;

    .prefix {
      height: var(--cds-spacing-200);
      flex-shrink: 0;

      svg {
        height: var(--cds-spacing-200);
        width: var(--cds-spacing-200);
      }
    }
  `,
  chipSkeletonText: css`
    border-radius: 0;
    height: 14px;
  `,
};

const AISummary = ({
  summary,
  suggestedQueries = [],
  isLoading = false,
  onQueryClick,
  showMockIndicator = false,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [effectiveCollapsedHeight, setEffectiveCollapsedHeight] = useState<string | undefined>(undefined);
  const [measuredMaxChipsPerRow, setMeasuredMaxChipsPerRow] = useState<number | null>(null);
  const summaryContentRef = useRef<HTMLDivElement>(null);
  const chipContainerRef = useRef<HTMLDivElement>(null);
  const track = useTracker();

  const visibilityTrackingRef = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'search_ai_summary',
      },
    },
    [summary]
  ) as React.MutableRefObject<HTMLDivElement | null>;

  const maxChipsPerRow = React.useMemo(() => {
    if (measuredMaxChipsPerRow !== null) {
      return Math.max(3, measuredMaxChipsPerRow);
    }

    return 3;
  }, [measuredMaxChipsPerRow]);

  const firstRowEndIndex = React.useMemo(() => {
    if (suggestedQueries.length <= maxChipsPerRow) {
      return suggestedQueries.length;
    }

    const firstRowCount = Math.min(maxChipsPerRow, suggestedQueries.length);
    const remainingCount = suggestedQueries.length - firstRowCount;

    if (Math.abs(firstRowCount - remainingCount) <= 1) {
      return Math.ceil(suggestedQueries.length / 2);
    }

    return firstRowCount;
  }, [suggestedQueries.length, maxChipsPerRow]);

  useEffect(() => {
    if (!chipContainerRef.current || suggestedQueries.length === 0) {
      return;
    }

    const measureChips = () => {
      const container = chipContainerRef.current;
      if (!container) return;

      const containerWidth = container.getBoundingClientRect().width;
      const allChipButtons = container.querySelectorAll('button');
      if (allChipButtons.length === 0) return;

      let totalWidth = 0;
      let chipsThatFit = 0;
      const gap = 8; // var(--cds-spacing-100) = 8px

      for (let i = 0; i < allChipButtons.length; i += 1) {
        const chip = allChipButtons[i] as HTMLElement;
        const chipWidth = chip.getBoundingClientRect().width;

        const widthWithGap = totalWidth + (i > 0 ? gap : 0) + chipWidth;

        if (widthWithGap <= containerWidth) {
          totalWidth = widthWithGap;
          chipsThatFit = i + 1;
        } else {
          break;
        }
      }

      const finalChipsThatFit = Math.max(3, chipsThatFit);
      if (finalChipsThatFit !== measuredMaxChipsPerRow) {
        setMeasuredMaxChipsPerRow(finalChipsThatFit);
      }
    };

    const timeoutId = setTimeout(measureChips, 0);

    const resizeObserver = new ResizeObserver(() => {
      measureChips();
    });

    if (chipContainerRef.current) {
      resizeObserver.observe(chipContainerRef.current);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedQueries, firstRowEndIndex]);

  // Reset measured chip count when queries change
  useEffect(() => {
    setMeasuredMaxChipsPerRow(null);
  }, [suggestedQueries]);

  // Check if content exceeds the position of the first link
  useEffect(() => {
    const calculateHeight = () => {
      if (summaryContentRef.current) {
        // Find the first link element within the summary content
        const firstLink = summaryContentRef.current.querySelector('a');
        const collapsedHeight = parseInt(AI_SUMMARY_COLLAPSED_HEIGHT, 10);
        const contentHeight = summaryContentRef.current.scrollHeight;

        let calculatedHeight = collapsedHeight;

        if (firstLink) {
          const containerRect = summaryContentRef.current.getBoundingClientRect();
          const linkRect = firstLink.getBoundingClientRect();
          const linkPositionFromTop = linkRect.top - containerRect.top;

          const linkPositionWithPadding = linkPositionFromTop + AI_SUMMARY_LINK_POSITION_PADDING;

          calculatedHeight = Math.max(collapsedHeight, linkPositionWithPadding);
        }
        setHasOverflow(contentHeight > calculatedHeight);
        setEffectiveCollapsedHeight(`${calculatedHeight}px`);
      }
    };

    // Calculate on mount and when summary changes
    calculateHeight();
    setIsExpanded(false);

    // Recalculate when container size changes (e.g., on window resize or layout changes)
    const resizeObserver = new ResizeObserver(() => {
      calculateHeight();
    });

    if (summaryContentRef.current) {
      resizeObserver.observe(summaryContentRef.current);
    }

    // Cleanup function
    return () => {
      resizeObserver.disconnect();
    };
  }, [summary]);

  if (isLoading) {
    return (
      <>
        <Grid container css={styles.container} data-testid="ai-summary-loading">
          <Grid item xs={12}>
            <div css={styles.skeletonContainer}>
              <div css={styles.skeletonHeader}>
                <AIGenerateBrandedIcon aria-hidden="true" size="large" />
                <Skeleton width="100px" height="24px" />
              </div>
              <Skeleton width="100%" height="24px" />
              <Skeleton width="100%" height="24px" />
              <Skeleton width="70%" height="24px" />
              <Skeleton width="70%" height="24px" />
            </div>
          </Grid>
          <Grid item xs={12}>
            <div css={styles.suggestedQueriesSection}>
              <Skeleton width="150px" height="24px" css={styles.suggestedQueriesLabel} />
              <div css={styles.chipSkeletonContainerWrapper}>
                <div css={styles.chipSkeletonContainer}>
                  <div className="prefix">
                    <AIGenerateBrandedIcon aria-hidden="true" size="small" />
                  </div>
                  <Skeleton width="120px" css={styles.chipSkeletonText} />
                </div>
                <div css={styles.chipSkeletonContainer}>
                  <div className="prefix">
                    <AIGenerateBrandedIcon aria-hidden="true" size="small" />
                  </div>
                  <Skeleton width="150px" css={styles.chipSkeletonText} />
                </div>
                <div css={styles.chipSkeletonContainer}>
                  <div className="prefix">
                    <AIGenerateBrandedIcon aria-hidden="true" size="small" />
                  </div>
                  <Skeleton width="140px" css={styles.chipSkeletonText} />
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
        <Divider />
      </>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <>
      <div ref={visibilityTrackingRef}>
        <Grid container css={styles.container} data-testid="ai-summary">
          <Grid item xs={12}>
            <div css={styles.header}>
              <AIGenerateBrandedIcon aria-hidden="true" />
              <Typography2 variant="bodySecondary" component="span">
                {_t('AI Overview')}
                {showMockIndicator && (
                  <Typography2
                    variant="bodySecondary"
                    component="span"
                    css={css`
                      margin-left: var(--cds-spacing-100);
                      color: var(--cds-color-yellow-600);
                    `}
                  >
                    {_t('(Mocked data)')}
                  </Typography2>
                )}
              </Typography2>
            </div>
          </Grid>

          <Grid item xs={12}>
            <div
              ref={summaryContentRef}
              css={styles.summaryWrapper(isExpanded, effectiveCollapsedHeight)}
              {...(!isExpanded &&
                hasOverflow && {
                  onClick: () => setIsExpanded(true),
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsExpanded(true);
                    }
                  },
                  role: 'button',
                  tabIndex: 0,
                  'aria-label': _t('Expand summary'),
                })}
            >
              <Typography2 variant="bodyPrimary" component="div" css={styles.summaryText}>
                {summary}
              </Typography2>
            </div>

            {hasOverflow && (
              <Button
                variant="ghost"
                size="small"
                css={styles.moreButton}
                onClick={() => {
                  const buttonName = isExpanded ? 'ai_summary_show_less' : 'ai_summary_show_more';
                  track('click_page_section', {
                    pageSection: {
                      sectionName: 'search_ai_summary',
                    },
                  });
                  track('click_button', {
                    button: {
                      name: buttonName,
                    },
                  });
                  setIsExpanded(!isExpanded);
                }}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? _t('Show less') : _t('Show more')}
              >
                {isExpanded ? _t('Show less') : _t('Show more')}
              </Button>
            )}
          </Grid>

          {suggestedQueries.length > 0 && (
            <Grid item xs={12}>
              <div css={styles.suggestedQueriesSection}>
                <Typography2 variant="subtitleMedium" component="p" css={styles.suggestedQueriesLabel}>
                  {_t('You might follow up with...')}
                </Typography2>
                <div ref={chipContainerRef} css={styles.chipContainer}>
                  {/* First row */}
                  <div css={styles.chipRow} data-chip-row>
                    {suggestedQueries.slice(0, firstRowEndIndex).map((query) => (
                      <ChipButton
                        key={query}
                        onClick={() => onQueryClick?.(query)}
                        prefix={<AIGenerateBrandedIcon />}
                        height={AI_SUMMARY_CHIP_BUTTON_HEIGHT}
                        trackingName="search_page_suggested_search"
                        trackingData={{
                          href: `${ON_SITE_SEARCH_PATH}?query=${query.toLowerCase().trim()}`,
                          suggestionSource: 'ai_summary',
                        }}
                      >
                        {query}
                      </ChipButton>
                    ))}
                  </div>
                  {/* Second row */}
                  {firstRowEndIndex < suggestedQueries.length && (
                    <div css={styles.chipRow}>
                      {suggestedQueries.slice(firstRowEndIndex).map((query) => (
                        <ChipButton
                          key={query}
                          onClick={() => onQueryClick?.(query)}
                          prefix={<AIGenerateBrandedIcon />}
                          height={AI_SUMMARY_CHIP_BUTTON_HEIGHT}
                          trackingName="search_page_suggested_search"
                          trackingData={{
                            href: `${ON_SITE_SEARCH_PATH}?query=${query.toLowerCase().trim()}`,
                            suggestionSource: 'ai_summary',
                          }}
                        >
                          {query}
                        </ChipButton>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Grid>
          )}
        </Grid>
      </div>
      <Divider />
    </>
  );
};

export default AISummary;
